import React, { type ComponentProps } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { Button, Modal, theme } from '@sg/garnish';

import { Loyalty } from '@order/features/loyalty';

import { useScanToPayModalSafeAreaInsets } from '../../hooks';

export const ScanToPayPointsModal = (props: ScanToPayPointsModalProps) => {
  const {
    isVisible,
    pointsRewards,
    disclaimer,
    refetchRewards,
    onGoToSgRewards,
    onRequestClose,
  } = props;

  const { formatMessage } = useIntl();
  const a11yLabel = formatMessage(messages.a11yLabel);

  const safeAreaStyle = useScanToPayModalSafeAreaInsets();

  return (
    <Modal
      size="medium"
      palette="neutral"
      fitHeight
      withSafeAreaView={false}
      visible={isVisible}
      onRequestClose={onRequestClose}
    >
      <View style={safeAreaStyle}>
        <Modal.CloseBtn
          palette="muted"
          onPress={onRequestClose}
          width={40}
          height={40}
        />

        <Loyalty.InfoBenefits
          isLoading={false}
          outerContainerStyle={styles.loyaltyInfoContainer}
          loyaltyInfoBenefits={pointsRewards}
          disclaimer={disclaimer}
          refetch={refetchRewards}
        />

        <Modal.Footer withoutTopBorder style={styles.footer}>
          <Button
            palette="primaryOutline"
            accessibilityRole="button"
            accessibilityLabel={a11yLabel}
            style={styles.cta}
            onPress={onGoToSgRewards}
          >
            <FormattedMessage {...messages.cta} />
          </Button>
        </Modal.Footer>
      </View>
    </Modal>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  cta: {
    defaultMessage: 'Go to SG Rewards',
    description: 'Scan to Pay | Points | CTA',
  },
  a11yLabel: {
    defaultMessage: 'Open rewards screen',
    description: 'Scan To Pay | Points Modal | CTA | Accessibility Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    alignItems: 'center',
  },
  cta: {
    marginTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
  },
  loyaltyInfoContainer: {
    marginTop: 0,
    marginBottom: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayPointsModalProps = {
  isVisible: boolean;
  pointsRewards: ComponentProps<
    typeof Loyalty.InfoBenefits
  >['loyaltyInfoBenefits'];
  disclaimer?: string;
  refetchRewards: () => void;
  onGoToSgRewards: () => void;
  onRequestClose: () => void;
};
